import { Injectable, inject } from '@angular/core';
import { CacheService } from '../services/cache.service';
import { TokenService } from '../services/token.service';
import { jwtTenantId } from '../utils/jwt.util';
import { CachedEndpoint, CachedEndpointOptions } from './cached-endpoint';

/** Kesh kaliti kim uchun bo'linadi. */
export type CacheScope =
  /** Tenantga bog'liq — biznes almashsa boshqa kesh (deyarli har doim shu). */
  | 'tenant'
  /** Hamma uchun bir xil: valyuta ro'yxati, do'kon turlari kabi umumiy lug'atlar. */
  | 'global';

export interface CreateCachedEndpointOptions<T> extends Omit<
  CachedEndpointOptions<T>,
  'scope' | 'freshMs' | 'maxAgeMs'
> {
  freshMs?: number;
  maxAgeMs?: number;
  scope?: CacheScope;
}

const DEFAULT_FRESH_MS = 5 * 60 * 1000; // 5 daqiqa
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 1 kun

/**
 * Keshlanadigan GET endpointlarni yaratadigan yagona joy.
 *
 * <p>
 * Servis o'zining "og'ir va kam o'zgaradigan" so'rovini shu yerda e'lon qiladi:
 * </p>
 *
 * <pre>
 * private readonly liteCache = inject(ApiCacheService).create&lt;StoreLiteDto[]&gt;({
 *   store: 'stores',
 *   key: 'lite',
 *   fetch: () =&gt; this.http.get&lt;...&gt;(url).pipe(map((r) =&gt; r.data ?? [])),
 * });
 * </pre>
 *
 * <p>
 * Kalitga tenant AVTOMATIK qo'shiladi. Busiz bir nechta do'koni bor egasi
 * biznesni almashtirganda oldingi biznesning ro'yxatini ko'rib turardi — bu
 * eng qimmat xato bo'lardi, shuning uchun uni chaqiruvchi qo'lida qoldirmadik.
 * </p>
 */
@Injectable({ providedIn: 'root' })
export class ApiCacheService {
  private readonly cache = inject(CacheService);
  private readonly tokens = inject(TokenService);

  /** Yaratilgan endpointlar — chiqishda hammasini tozalash uchun. */
  private readonly endpoints: CachedEndpoint<unknown>[] = [];

  create<T>(options: CreateCachedEndpointOptions<T>): CachedEndpoint<T> {
    const scope: () => string | null =
      options.scope === 'global' ? () => null : () => this.currentTenant();

    const endpoint = new CachedEndpoint<T>(this.cache, {
      ...options,
      freshMs: options.freshMs ?? DEFAULT_FRESH_MS,
      maxAgeMs: options.maxAgeMs ?? DEFAULT_MAX_AGE_MS,
      scope,
    });

    this.endpoints.push(endpoint as CachedEndpoint<unknown>);
    return endpoint;
  }

  /**
   * Hamma keshni o'chiradi — tizimdan chiqishda.
   *
   * Ro'yxatdagi endpointlarni aylanib chiqish YETARLI EMAS: ular faqat shu
   * sessiyada ishlatilgan servislarni biladi, IndexedDB'da esa oldingi
   * sessiyalardan qolgan yozuvlar ham bor. Shuning uchun bo'limlar butunlay
   * tozalanadi, endpointlar esa xotiradagi nusxasini tashlaydi.
   *
   * <p>
   * `Promise` qaytaradi, chunki chaqiruvchilardan biri — sozlamalardagi "keshni
   * tozalash" — darhol sahifani yangilaydi. Kutilmasa, `location.reload()`
   * IndexedDB tranzaksiyasini yarim yo'lda uzib, kesh joyida qolib ketardi.
   * Tizimdan chiqishda esa kutish shart emas.
   * </p>
   */
  async clearAll(): Promise<void> {
    for (const endpoint of this.endpoints) endpoint.reset();
    await this.cache.clearAll();
  }

  /**
   * Tenant yo'q bo'lsa (Miyzan xodimlari — tokenda `TenantId` bo'lmaydi) alohida
   * bo'lim ishlatiladi: `global` bilan bir xil qilinsa, tenantga bog'liq kesh
   * bilan tenantsiz kesh bitta kalitga tushib qolardi.
   */
  private currentTenant(): string {
    return jwtTenantId(this.tokens.getAccessToken()) ?? 'no-tenant';
  }
}
